/** external */
import _ from "lodash";
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  IconButton,
  Tooltip,
  Dialog,
  Dropdown,
  DropdownMenu,
} from "@toddle-edu/ds-web";
import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";
import {
  DeleteOutlined,
  AssessmentApplicableOutlined,
  MagicWandThreeStarsOutlined,
  DotsVerticalOutlined,
  AssessmentOutlined,
} from "@toddle-edu/ds-icons";
import classNames from "classnames";
import { connect } from "react-redux";

/** internal */
import { FormTextInput } from "UIComponents/InputTextField";
import { useI18n } from "Hooks";
import {
  WORKSHEET_MODES,
  MODE_TEACHER_CREATE,
  MODE_TEACHER_VIEW,
  MODE_TEACHER_EVALUATE,
  MODE_STUDENT_ATTEMPT,
  MODE_STUDENT_VIEW,
  MODE_STUDENT_EVALUATE,
  QUESTION_UID_MULTI_PART,
  MODE_TEACHER_PREVIEW,
  MODE_ASSESSMENT_VIEW,
  MODE_LIBRARY_VIEW,
  MODE_RESTRICTED_ASSESSMENT_VIEW,
  QUESTION_TYPE_DETAILS,
  MODE_EVALUATION_INSIGHT,
  AI_SUPPORTED_QUESTION_TYPES,
  QUESTION_TYPES_V2,
  RICHTEXT_VIEW_STYLES,
} from "Worksheet/modules/WorksheetConstants";
import {
  checkScoreFieldValidity,
  getResolvedFieldLabel,
  getQuestionHeaderOptions,
  isStudentEndMode,
} from "Worksheet/modules/WorksheetUtils";
import { getIsWorksheetAIEnabled } from "Worksheet/modules/WorksheetFeatureFlags";
import { getStyleStrippedText } from "Utils";
import AssessmentToolModal from "WorksheetInsights/components/AssessmentToolModal";
import { getCurrentCurriculumProgramType } from "modules/CommonSelectors";

/** relative */
import classes from "./Header.scss";
import TickComponent from "../../SideBar/TickComponent";
import QuestionFieldComponent from "../QuestionFieldComponent";
import AddToLibraryButton from "./AddToLibraryButton";
import ReorderButtons from "../SubQuestions/ReorderButtons";

const styles = RICHTEXT_VIEW_STYLES;

const Header = props => {
  const {
    questionIndex,
    label,
    score,
    scoreField,
    showDeleteOption,
    isActiveQuestion,
    mode,
    deleteQuestion,
    updateFields,
    showAssessmentToolOption,
    evaluatedScore,
    isQuestionEvaluated,
    onAssessmentToolClick,
    isScoreInputDisabled,
    questionTypeUid,
    isFromLibrary,
    toggleAddToLibraryModal,
    isSubQuestion,
    isAddedToSchoolLibrary,
    isFromInternalTool,
    isFromToddleLibraryPortal,
    portalType,
    labelIcon,
    duplicateQuestion,
    isSubQuestionActive,
    assessmentTools,
    toolInsights,
    assignedStudents,
    handleShowQuestionAssistant,
    type,
    isGeneratingAIQuestions,
    assessmentToolsEvaluationDetails,
    isLibraryStudentPreview,
    onMoveUp,
    onMoveDown,
    isFirstSubQuestion,
    isLastSubQuestion,
    isCreateMode,
  } = props;

  const { t } = useI18n(["common", "worksheet"]);
  const isWorksheetAIEnabled = getIsWorksheetAIEnabled();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAssessmentToolsModal, setShowAssessmentToolsModal] = useState(
    false
  );
  const [isDuplicatingQuestion, setIsDuplicatingQuestion] = useState(false);

  const isEvaluated = isQuestionEvaluated && !_.isNaN(evaluatedScore);
  const filteredAssessmentToolsForInsights = _.filter(
    assessmentTools?.edges,
    ({ node }) => {
      const currentTool = _.find(ASSESSMENT_TOOLS, {
        type: node?.assessmentToolType,
      });

      return _.get(currentTool, "hasInsight", false);
    }
  );
  const filteredAssessmentToolInsights = {
    edges: filteredAssessmentToolsForInsights,
    totalCount: _.size(filteredAssessmentToolsForInsights),
    __typename: "AssessmentToolConnection",
  };

  const showAssessmentToolsInsights =
    _.isEqual(mode, MODE_EVALUATION_INSIGHT) &&
    _.size(filteredAssessmentToolsForInsights) > 0;

  const evaluatedScoreText =
    isEvaluated || questionTypeUid == QUESTION_UID_MULTI_PART
      ? evaluatedScore
      : "-";

  const toggleDeleteDialog = e => {
    e?.stopPropagation?.();
    setShowDeleteDialog(!showDeleteDialog);
  };

  const onDeleteClick = () => {
    deleteQuestion();
    toggleDeleteDialog();
  };

  const onQuestionDropdownClick = async ({ key }) => {
    switch (key) {
      case "duplicate":
        setIsDuplicatingQuestion(true);
        await duplicateQuestion();
        setIsDuplicatingQuestion(false);
        break;

      case "addToLibrary":
        isAddedToSchoolLibrary ? _.noop : toggleAddToLibraryModal();
        break;

      default:
        break;
    }
  };

  const updateScore = params => {
    const key = "score";
    const newValue = _.get(params, key);

    const fields = [
      {
        id: _.get(scoreField, "id"),
        value: newValue,
      },
    ];
    if (
      (newValue && !checkScoreFieldValidity(newValue)) ||
      _.toInteger(newValue) > 100
    )
      return;

    updateFields({
      fields,
      isDebounce: true,
      isMutation: false,
    });
  };
  const updateScoreOnBlur = ({ params }) => {
    const key = "score";
    const newValue = _.get(params, key);

    const fields = [
      {
        id: _.get(scoreField, "id"),
        value: newValue,
      },
    ];
    if (
      (newValue && !checkScoreFieldValidity(newValue)) ||
      _.toInteger(newValue) > 100
    )
      return;

    updateFields({
      fields,
      isDebounce: false,
      isMutation: true,
    });
  };

  const showScoreContainer = _.includes(
    [
      MODE_TEACHER_CREATE,
      MODE_STUDENT_ATTEMPT,
      MODE_STUDENT_VIEW,
      MODE_TEACHER_PREVIEW,
      MODE_ASSESSMENT_VIEW,
      MODE_RESTRICTED_ASSESSMENT_VIEW,
      MODE_LIBRARY_VIEW,
      MODE_EVALUATION_INSIGHT,
    ],
    mode
  );
  const showAddToLibraryButton =
    _.get(QUESTION_TYPE_DETAILS[questionTypeUid], "canAddToLibrary", true) &&
    !isSubQuestion &&
    !isFromInternalTool &&
    !isFromToddleLibraryPortal &&
    isActiveQuestion &&
    mode == MODE_TEACHER_CREATE;

  const questionsOverlay = (
    <DropdownMenu
      options={getQuestionHeaderOptions({
        t,
        isSubQuestion: isSubQuestion && !showAddToLibraryButton,
        isAddedToSchoolLibrary,
        showAddToLibraryButton,
        isDuplicatingQuestion,
      })}
      onClick={onQuestionDropdownClick}
    ></DropdownMenu>
  );
  const showMoreButton =
    _.isEqual(mode, MODE_TEACHER_CREATE) &&
    // If anyone of these is true, show more button will not be shown
    !(isFromInternalTool || isFromToddleLibraryPortal);

  const isLibraryViewMode = _.isEqual(mode, MODE_LIBRARY_VIEW);
  const isMultipartQuestion = questionTypeUid == QUESTION_UID_MULTI_PART;
  const isNotLibraryViewSubQuestion = !(
    (isLibraryViewMode || isLibraryStudentPreview) &&
    !isSubQuestion
  );

  const isStudentRevamp = isStudentEndMode(mode);

  const indexClasses = classNames(classes.index, {
    [classes.libraryIndex]: isFromLibrary,
    "heading-5": !isSubQuestion && !isFromLibrary && isActiveQuestion,
    "heading-6": isSubQuestion && !isFromLibrary && isActiveQuestion,
    [classes.revampQuestionIndex]: isStudentRevamp,
    [classes.oldQuestionIndex]: !isStudentRevamp,
  });

  const labelClasses = classNames(classes.label, {
    "text-body-1": !isFromLibrary && isActiveQuestion,
    "text-body-2": isFromLibrary && isActiveQuestion,
    [classes.labelLibrary]: isFromLibrary && isActiveQuestion,
    "text-body-l": !isActiveQuestion,
  });

  const richTextLabelClasses = classNames({
    ["w-full"]: true,
    ["text-body-1"]: _.isEqual(mode, MODE_EVALUATION_INSIGHT),
    ["text-body-l"]: isStudentRevamp,
    [classes.minWidth0]: true,
  });

  const scoreClasses = classNames(classes.score, {
    "text-body-1":
      !isFromLibrary &&
      mode !== MODE_EVALUATION_INSIGHT &&
      isActiveQuestion &&
      !isStudentRevamp,
    "text-body-2": isFromLibrary || mode == MODE_EVALUATION_INSIGHT,
    [classes.scoreLabel]: mode == MODE_EVALUATION_INSIGHT && isActiveQuestion,
    [classes.scoreCreateModeInActiveRevamped]:
      !isActiveQuestion || isStudentRevamp,
    "text-body": !isActiveQuestion,
    "text-label-3": isStudentRevamp,
  });

  const scoreInputLabelClasses = classNames("text-body-2", {
    [classes.scoreLabel]: true,
  });

  const containerClasses = classNames({
    [classes.container]: _.includes(
      [
        MODE_TEACHER_CREATE,
        MODE_ASSESSMENT_VIEW,
        MODE_RESTRICTED_ASSESSMENT_VIEW,
        MODE_LIBRARY_VIEW,
      ],
      mode
    ),
    [classes.studentViewContainer]: !_.includes(
      [
        MODE_TEACHER_CREATE,
        MODE_ASSESSMENT_VIEW,
        MODE_RESTRICTED_ASSESSMENT_VIEW,
        MODE_LIBRARY_VIEW,
      ],
      mode
    ),
  });

  const leftContainerClasses = classNames({
    [classes.leftContent]: _.includes(
      [
        MODE_TEACHER_CREATE,
        MODE_ASSESSMENT_VIEW,
        MODE_RESTRICTED_ASSESSMENT_VIEW,
        MODE_LIBRARY_VIEW,
      ],
      mode
    ),
    [classes.leftContainer]: !_.includes(
      [
        MODE_TEACHER_CREATE,
        MODE_ASSESSMENT_VIEW,
        MODE_RESTRICTED_ASSESSMENT_VIEW,
        MODE_LIBRARY_VIEW,
      ],
      mode
    ),
    [classes.leftContentLibrary]: isFromLibrary,
    [classes.alignItemsNormal]: _.isEqual(mode, MODE_EVALUATION_INSIGHT),
  });

  const rightContentClasses = classNames(classes.rightContent, {
    [classes.toddleLibraryItem]: isLibraryViewMode && !isSubQuestion,
  });

  const closeAssessmentToolsModal = () => {
    setShowAssessmentToolsModal(false);
  };
  const openAssessmentToolsModal = () => {
    setShowAssessmentToolsModal(true);
  };

  const getCustomTextStyle = () => {
    return isStudentRevamp
      ? styles.isStudentRevampStyle
      : styles.richTextViewModeV2;
  };

  const isAiSupportedQuestion = _.includes(AI_SUPPORTED_QUESTION_TYPES, type);

  const showAIAssistant =
    isCreateMode &&
    !isSubQuestion &&
    !isFromInternalTool &&
    !isFromToddleLibraryPortal &&
    isWorksheetAIEnabled;

  return (
    <div
      className={containerClasses}
      role="region"
      aria-labelledby="question-header"
    >
      <div
        className={leftContainerClasses}
        role="heading"
        aria-level="1"
        id="question-header"
      >
        {/* question index */}
        {!(isFromInternalTool && !isSubQuestion) &&
        !_.isEqual(mode, MODE_EVALUATION_INSIGHT) &&
        isNotLibraryViewSubQuestion ? (
          <h6 className={indexClasses} role="presentation">
            {t("worksheet:question_abbreviation_with_number", {
              number: questionIndex,
            })}
          </h6>
        ) : null}
        {isFromLibrary && isNotLibraryViewSubQuestion ? (
          <span className={classes.separator}>-</span>
        ) : null}
        {_.isEqual(mode, MODE_EVALUATION_INSIGHT) ? (
          <div className={classes.labelIconClasses}>{labelIcon}</div>
        ) : null}
        {/* question type label */}
        {_.includes(
          [
            MODE_TEACHER_CREATE,
            MODE_ASSESSMENT_VIEW,
            MODE_RESTRICTED_ASSESSMENT_VIEW,
          ],
          mode
        ) ||
        (isLibraryViewMode && !isMultipartQuestion) ? (
          <div className={labelClasses}>{t(label)}</div>
        ) : label ? (
          <div className={richTextLabelClasses}>
            <QuestionFieldComponent
              type={"RichText_Ported"}
              fieldValue={getStyleStrippedText({
                text: label,
                type: "DOCUMENT_EDITOR",
              })}
              mode={mode}
              fieldMode={"view"}
              portalType={portalType}
              isFromInternalTool={isFromInternalTool}
              customTextStyle={getCustomTextStyle()}
            />
          </div>
        ) : null}
      </div>
      <div className={rightContentClasses}>
        {/* score */}
        {showScoreContainer ? (
          <>
            <div className={classes.scoreContainer}>
              {mode == MODE_TEACHER_CREATE && isActiveQuestion ? (
                <div className={classes.scoreInput}>
                  <div className={scoreInputLabelClasses}>
                    {getResolvedFieldLabel(t("common:marks"), true)}
                  </div>
                  <div className={classes.scoreInputContainer}>
                    <FormTextInput
                      size="medium"
                      name={"score"}
                      value={
                        isScoreInputDisabled
                          ? score
                          : _.get(scoreField, "value", "")
                      }
                      updateInputField={updateScore}
                      onBlurInputField={updateScoreOnBlur}
                      autoComplete={"off"}
                      disabled={isScoreInputDisabled}
                      testId="worksheet-question-header-scoreInput"
                      aria-label={t("common:score_with_label", {
                        label: t("common:assign"),
                      })}
                    />
                  </div>
                </div>
              ) : (
                <div className={scoreClasses}>
                  {t("common:mark_with_number", { count: score })}
                </div>
              )}
            </div>

            {/* ADDING SEPARATOR FOR NEW DESIGN */}
            {isActiveQuestion && (
              <div className={classes.revampedSeparator}></div>
            )}
          </>
        ) : null}
        {/* assessment tools */}
        {showAssessmentToolsInsights ? (
          <div>
            <IconButton
              icon={<AssessmentOutlined variant="subtle" />}
              dsVersion="2.0"
              type="outlined"
              variant="neutral"
              size="small"
              _legacy_variant="outlined-subtle"
              _legacy_size="x-small"
              onClick={openAssessmentToolsModal}
              testId="worksheet-question-header-openAssessmentToolsModal"
              aria-label={t("classroom:assessment_tools")}
            />
          </div>
        ) : null}

        {_.includes([MODE_TEACHER_VIEW, MODE_TEACHER_EVALUATE], mode) ? (
          <div className={classes.marksViewContainer}>
            <div className={classes.marksContainer}>
              <span className="heading-5">{evaluatedScoreText}</span>
              <span className="text-body-2">
                /{t("common:mark_with_number", { count: score })}
              </span>
            </div>
            <TickComponent isCompleted={isEvaluated} />
          </div>
        ) : null}
        {mode == MODE_STUDENT_EVALUATE ? (
          <div className={classes.scoreTagContainer}>
            <span className="heading-5">{evaluatedScoreText}</span>
            <span className="text-body-2">
              /{t("common:mark_with_number", { count: score })}
            </span>
          </div>
        ) : null}

        {showAIAssistant ? (
          !isAiSupportedQuestion ? (
            <Tooltip
              tooltip={t("worksheet:disabled_question_assistant_tooltip_label")}
            >
              <IconButton
                dsVersion="2.0"
                type="plain"
                variant="neutral"
                _legacy_variant={"plain"}
                icon={<MagicWandThreeStarsOutlined variant="subtle" />}
                size="x-small"
                shouldStopPropagation={true}
                testId="worksheet-question-header-worksheetAssistantDropdown-disabled"
                disabled={!isAiSupportedQuestion}
                aria-label={t("worksheet:chat_here_with_toddle_ai")}
              />
            </Tooltip>
          ) : (
            <IconButton
              dsVersion="2.0"
              type="plain"
              variant="neutral"
              _legacy_variant={"plain"}
              icon={<MagicWandThreeStarsOutlined variant="link" />}
              size="x-small"
              shouldStopPropagation={true}
              testId="worksheet-question-header-worksheetAssistantDropdown"
              onClick={handleShowQuestionAssistant}
              disabled={isGeneratingAIQuestions}
              className={classes.assistantAvatar}
              aria-label={t("worksheet:chat_here_with_toddle_ai")}
            />
          )
        ) : null}

        {isActiveQuestion || isSubQuestionActive ? (
          <>
            {/* delete */}
            {showDeleteOption ? (
              <Tooltip
                tooltip={t("common:delete_with_label", {
                  label: t("common:question"),
                })}
              >
                <IconButton
                  dsVersion="2.0"
                  icon={<DeleteOutlined />}
                  variant="neutral"
                  type="plain"
                  _legacy_variant="plain"
                  size="x-small"
                  onClick={toggleDeleteDialog}
                  testId="worksheet-question-header-deleteDialog"
                  aria-label={t("common:delete_with_label", {
                    label: t("common:question"),
                  })}
                />
              </Tooltip>
            ) : null}
            {/* reorder subquestions */}
            {isSubQuestion && isFromInternalTool ? (
              <ReorderButtons
                isFirstQuestion={isFirstSubQuestion}
                isLastQuestion={isLastSubQuestion}
                onMoveUp={onMoveUp}
                onMoveDown={onMoveDown}
              />
            ) : null}

            {/* More Option */}
            {showMoreButton ? (
              <Dropdown overlay={questionsOverlay}>
                <IconButton
                  icon={<DotsVerticalOutlined />}
                  dsVersion="2.0"
                  variant="neutral"
                  type="plain"
                  _legacy_variant="plain"
                  size="x-small"
                  testId={`worksheet-question-header-showMore-${questionIndex}`}
                  aria-label={t("common:label_count", {
                    label: t("assessmentEvaluation:options"),
                    count: t("common:more"),
                  })}
                />
              </Dropdown>
            ) : showAddToLibraryButton ? (
              <AddToLibraryButton
                isAddedToSchoolLibrary={isAddedToSchoolLibrary}
                onClick={toggleAddToLibraryModal}
                iconVariant="plain"
                iconSize="x-small"
                iconType="plain"
                aria-label={t("common:add_to_label", {
                  label: t("common:library"),
                })}
              />
            ) : null}
          </>
        ) : null}
        {showAssessmentToolOption ? (
          <div className={classes.assessmentContainer}>
            <Tooltip tooltip={t("common:le_assessmentTools_label")}>
              <IconButton
                dsVersion="2.0"
                size={!isStudentRevamp ? "x-small" : "small"}
                variant="neutral"
                type="outlined"
                _legacy_variant="plain"
                _legacy_size={"x-small"}
                icon={
                  isStudentRevamp ? (
                    <AssessmentOutlined variant="subtle" />
                  ) : (
                    <AssessmentApplicableOutlined />
                  )
                }
                onClick={onAssessmentToolClick}
                testId="worksheet-question-header-assessmentToolsOption"
                aria-label={t("classRoom:assessment_tools")}
              />
            </Tooltip>
          </div>
        ) : null}
      </div>
      {showDeleteDialog ? (
        <Dialog
          variant="delete-dialog"
          title={t("common:delete_with_label", {
            label: t("common:question"),
          })}
          message={t("common:delete_msg_with_this_label_lowercase", {
            label: t("common:question"),
          })}
          buttonOneText={t("common:cancel")}
          buttonTwoText={t("common:confirm")}
          onButtonTwoClick={onDeleteClick}
          onButtonOneClick={toggleDeleteDialog}
          onClose={toggleDeleteDialog}
          testId="worksheet-question-header-deleteDialog"
        />
      ) : null}
      {/* Assessment Tools Modal */}
      <AssessmentToolModal
        showAssessmentToolsModal={showAssessmentToolsModal}
        closeAssessmentToolsModal={closeAssessmentToolsModal}
        assessmentTools={filteredAssessmentToolInsights}
        toolInsights={toolInsights}
        assignedStudents={assignedStudents}
        label={label}
        labelIcon={labelIcon}
        assessmentToolsEvaluationDetails={assessmentToolsEvaluationDetails}
      />
    </div>
  );
};

const mapStateToProps = state => {
  const curriculumType = getCurrentCurriculumProgramType(state);

  const isGeneratingAIQuestions = _.get(
    state,
    "worksheet.generatingAIQuestions",
    false
  );

  return { curriculumType, isGeneratingAIQuestions };
};

Header.propTypes = {
  questionIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string,
  score: PropTypes.number,
  scoreField: PropTypes.object,
  showDeleteOption: PropTypes.bool,
  mode: PropTypes.oneOf(WORKSHEET_MODES),
  isActiveQuestion: PropTypes.bool,
  deleteQuestion: PropTypes.func,
  updateFields: PropTypes.func,
  showAssessmentToolOption: PropTypes.bool,
  evaluatedScore: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onAssessmentToolClick: PropTypes.func,
  isScoreInputDisabled: PropTypes.bool,
  isQuestionEvaluated: PropTypes.bool,
  questionTypeUid: PropTypes.string,
  isFromLibrary: PropTypes.bool,
  toggleAddToLibraryModal: PropTypes.func,
  isSubQuestion: PropTypes.bool,
  isAddedToSchoolLibrary: PropTypes.bool,
  isFromInternalTool: PropTypes.bool,
  handleShowQuestionAssistant: PropTypes.func,
  type: PropTypes.oneOfType(QUESTION_TYPES_V2),
  assessmentToolsEvaluationDetails: PropTypes.object,
  isLibraryStudentPreview: PropTypes.bool,
};

Header.defaultProps = {
  isScoreInputDisabled: false,
  isQuestionEvaluated: false,
  toggleAddToLibraryModal: _.noop,
  isSubQuestion: false,
  isFromInternalTool: false,
  assessmentToolsEvaluationDetails: {},
  isLibraryStudentPreview: false,
};

export default connect(mapStateToProps, null)(Header);
